import { LicenseConfirmationBlock } from "../licenseConfirmationBlock/LicenseConfirmationBlock";
import {
  LicenseConfirmationBlock as LicenseConfirmationBlockV2,
  type LicenseConfirmationFormRef,
} from "../LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import { type LicenseConfirmationData, type LicenseConfirmationItem } from "../../../../../interfaces";
import { type FC, type MutableRefObject, useEffect } from "react";
import { Loader } from "semantic-ui-react";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { fetchAssignmentInfo, licensesResetAction } from "../../state/thunks/assignmentModalLicensingThunk";
import { type RootState } from "../../../../Application/globaltypes/redux";
import { uniq } from "lodash";
import { Context } from "../../../../../enums/licenseContext";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../featureFlags";
import { Typography } from "../../../../../components/common/Typography/Typography";
import { selectContentPacksPackIds } from "../../state/selectors";

import styles from "./licenseConfirmationInfo.module.scss";

interface LicenseConfirmationInfoProps {
  info?: LicenseConfirmationData;
  onIsDataValidChange?: (isValid: boolean) => void;
  licenseContext?: Context;
  setIssueLicensePackIds?: (packIds: number[]) => void;
  licenseConfirmationFormRef?: MutableRefObject<LicenseConfirmationFormRef>;
}

export type LicenseConfirmationInfoAllProps = LicenseConfirmationInfoProps & PropsFromRedux;

export const LicenseConfirmationInfo: FC<LicenseConfirmationInfoAllProps> = ({
  info,
  items,
  isLoading,
  packIds,
  packIdForUserGroupLicensing,
  getAssignmentInfo,
  licensesReset,
  error,
  onIsDataValidChange,
  licenseContext = Context.AddPeopleToContent,
  setIssueLicensePackIds,
  licenseConfirmationFormRef,
  isPurchaseNeeded,
}) => {
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);

  const getPackIds = (idsContext: Context) => {
    switch (idsContext) {
      case Context.AddPackToUsersOrGroups:
        return [packIdForUserGroupLicensing];
      case Context.AddPacksToUserOrGroup:
        return info?.packIds;
      default:
        return packIds;
    }
  };

  useEffect(() => {
    const packIdsActual = getPackIds(licenseContext);
    setIssueLicensePackIds && packIdsActual && setIssueLicensePackIds(uniq(packIdsActual));

    getAssignmentInfo(licenseContext, { ...info, packIds: packIdsActual }, additionalPurchaseEnabled);

    return () => {
      licensesReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (additionalPurchaseEnabled) return;

    const hasExpiredNeededLicenses = items.some(
      (i: LicenseConfirmationItem) => i.isAccountPackLicenseExpired && i.neededLicenses > 0,
    );
    const isValid = !error && !isLoading && !hasExpiredNeededLicenses;
    onIsDataValidChange?.(isValid);
  }, [error, isLoading, items, additionalPurchaseEnabled, onIsDataValidChange]);

  const isWarningMode = items.some((i: LicenseConfirmationItem) => i.purchasingLicenses > 0);

  if (error) {
    return <div data-testid="error-info">Error loading data!</div>;
  }

  return (
    <div data-testid="confirmation-info" className={styles.root}>
      <div data-testid="confirmation-text" className={styles.header}>
        {!additionalPurchaseEnabled && isWarningMode && (
          <Typography variant="title1" component="div">
            These changes will consume licenses which will incur additional charges.
          </Typography>
        )}
        <Typography variant="title1" component="div">
          You are issuing the following licenses:
        </Typography>
      </div>
      {additionalPurchaseEnabled ? (
        <LicenseConfirmationBlockV2
          items={items}
          licenseConfirmationFormRef={licenseConfirmationFormRef}
          onIsDataValidChange={onIsDataValidChange}
          isPurchaseNeeded={isPurchaseNeeded}
        />
      ) : (
        <LicenseConfirmationBlock items={items} />
      )}
      <Loader active={isLoading} />
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
    items: state.licensing?.availableLicenses?.items ?? [],
    isLoading: state.licensing?.availableLicenses?.isLoading ?? false,
    error: state.licensing?.availableLicenses?.error,
    packIds: selectContentPacksPackIds(state),
    packIdForUserGroupLicensing: state.packs.purchasedPack.packPurchasedView.pack.id,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: any) => ({
  getAssignmentInfo: bindActionCreators(fetchAssignmentInfo, dispatch),
  licensesReset: bindActionCreators(licensesResetAction, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(LicenseConfirmationInfo);
export default ConnectedComponent;
